import { defineStore } from "pinia"
import { ref, computed } from "vue"
import { useRouter } from "vue-router"
import { formatAccountPermissions } from "@/api/account/utils"
import { useJourneyStore } from "@/stores/AutomatedMarketingGroup/journeys"
import { reset401Flag } from "@/composables/useRequest"

interface UserInfo {
    uuid: string
    name: string
    isActive: boolean
    department: string
    departmentUuid: string
    role: string
    roleName: string
    email: string
    level: number
}

interface PermissionResult {
    permissionId: string
    isActive: boolean
    effectiveRange?: string[]
    statusBlacklist?: string[]
    statusWhitelist?: string[]
}

interface UserPermissions {
    results: PermissionResult[]
}

interface UserToken {
    access: string
    refresh: string
}

export const useUserStore = defineStore("user", () => {
    const router = useRouter()

    const userInfo = ref<UserInfo | null>(JSON.parse(localStorage.getItem("userInfo") ?? "null"))
    const userPermissions = ref<UserPermissions | null>(
        JSON.parse(localStorage.getItem("userPermissions") ?? "null")
    )
    const userToken = ref<UserToken | null>(JSON.parse(localStorage.getItem("userToken") ?? "null"))

    const formatUserPermissions = computed(() =>
        userPermissions.value ? formatAccountPermissions(userPermissions.value) : []
    )
    const formatUserInfo = computed(() => userInfo.value)
    const formatUserToken = computed(() => userToken.value?.access)
    const formatUserRefreshToken = computed(() => userToken.value?.refresh)

    function setUserInfo(info: UserInfo | null) {
        if (info) {
            userInfo.value = info
            localStorage.setItem("userInfo", JSON.stringify(info))
            return
        }
        userInfo.value = null
        localStorage.removeItem("userInfo")
    }

    function setUserPermissions(permissions: UserPermissions | null) {
        if (permissions) {
            userPermissions.value = permissions
            localStorage.setItem("userPermissions", JSON.stringify(permissions))
            return
        }
        userPermissions.value = null
        localStorage.removeItem("userPermissions")
    }

    function setUserToken(token: UserToken | null) {
        if (token) {
            reset401Flag()
            userToken.value = token
            localStorage.setItem("userToken", JSON.stringify(token))
            return
        }
        userToken.value = null
        localStorage.removeItem("userToken")
    }

    // 新增 store 時需在此補上對應的 reset() 呼叫
    function resetAllStores() {
        reset()
        useJourneyStore().reset()
    }

    async function logout() {
        setUserToken(null)
        await router.push({ name: "login" })
        setUserInfo(null)
        setUserPermissions(null)
        resetAllStores()
    }

    function reset() {
        userInfo.value = null
        userPermissions.value = null
        userToken.value = null
    }

    return {
        userInfo,
        userPermissions,
        userToken,
        formatUserPermissions,
        formatUserInfo,
        formatUserToken,
        formatUserRefreshToken,
        setUserInfo,
        setUserPermissions,
        setUserToken,
        logout,
        reset,
    }
})
